import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import type { PatientVital } from '@/types/vitals-api';

/**
 * PUT /api/Patients/updateVitals
 *
 * NOTE the lowercase `u` in `updateVitals` — backend casing quirk
 * (CreateVitals + GetVitals are PascalCase; only update is camelCase).
 * `endpoint` literal kept lowercase to match the wire exactly.
 *
 * Used when this encounter already has vitals (`readings[0].identifire`
 * is non-null after the initial CreateVitals call). Each row in the
 * body carries its server-assigned `identifire`; the backend keys on
 * that to update the right entry.
 *
 * Wire shape verified by dev curl 2026-05-15:
 *   - Body is a BARE ARRAY of `PatientVital` rows (no envelope).
 *   - Every row has `identifire` set (server-assigned GUID).
 *   - `isDeleted: false` (rows aren't soft-deleted via this call —
 *     deleting a row would be a separate operation).
 *
 * PATIENT-SAFETY (PROD-795 PR 1 Samer review §5):
 * ──────────────────────────────────────────────
 * We ship `vitalNormalStatus: null` + `vitalAbnormalPercentage: null`
 * on update so the server's vitals normalization service is the SOLE
 * authority on threshold evaluation. Flutter's current update curl
 * echoes back the OLD vitalNormalStatus from the in-memory state —
 * that's a stale-flag risk if a user edits a value but the server's
 * recomputation lags. Clearing them forces the server to recompute
 * from the new values.
 *
 * On success, invalidates `['encounter-vitals', encounterId, patientId]`
 * so the read-side query refetches the server's freshly-computed
 * normal/abnormal flags.
 */

interface UpdateVitalsVariables {
  patientId: string;
  encounterId: string;
  vitals: PatientVital[];
}

export function useUpdateVitals() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, UpdateVitalsVariables>({
    mutationFn: ({ vitals }) =>
      put<unknown>('/api/Patients/updateVitals', vitals),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['encounter-vitals', vars.encounterId, vars.patientId],
      });
    },
  });
}
